import React from 'react';
import { KeySquare, UserRoundCheck, LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AboutFeature {
  icon: LucideIcon;
  title: string;
  description: string;
}

interface ContactInfo {
  label: string;
  value: React.ReactNode;
}

interface AboutSectionProps {
  logoText?: string;
  title?: string;
  description?: string;
  features?: AboutFeature[];
  image?: string;
  imageAlt?: string;
  contactInfo?: ContactInfo[];
  showContactInfo?: boolean;
  className?: string;
}

const defaultFeatures: AboutFeature[] = [
  {
    icon: KeySquare,
    title: "Consultoria estratégica imobiliária",
    description: "Apoiamos você com orientações técnicas e mercadológicas para tomar decisões seguras na hora de comprar ou vender."
  },
  {
    icon: UserRoundCheck,
    title: "Especialistas na região de Campinas",
    description: "Conhecemos cada detalhe dos bairros da cidade e usamos isso para indicar oportunidades alinhadas ao seu perfil."
  }
];

const defaultContactInfo: ContactInfo[] = [
  {
    label: "E-mail",
    value: "desordi@reformassemgrilo.com.br"
  },
  {
    label: "Telefone",
    value: "(19) 99603-1818"
  },
  {
    label: "Endereço",
    value: (
      <>
        R. Dr. Liráucio Gomes, 110 - Cambuí
        <br />
        Campinas - SP
      </>
    )
  }
];

const AboutSection: React.FC<AboutSectionProps> = ({
  logoText = "[logo]",
  title = "Acreditamos em atendimento próximo, imóveis bem selecionados e processos sem enrolação.",
  description = "Somos uma imobiliária de Campinas que acredita em atendimento próximo, imóveis bem selecionados e processos sem enrolação.",
  features = defaultFeatures,
  image = "https://api.builder.io/api/v1/image/assets/TEMP/58982638bf78af3dc6a017ae8cb2d62a2cfc9055?width=1290",
  imageAlt = "Real estate team",
  contactInfo = defaultContactInfo,
  showContactInfo = true,
  className
}) => {
  return (
    <section
      className={cn(
        "flex flex-col gap-6 items-start p-4 lg:p-20 w-full bg-white",
        className,
      )}
    >
      {/* Container Principal - seguindo exatamente o Figma */}
      <div className="bg-gray-light flex flex-col gap-8 lg:gap-12 items-center px-0 py-8 lg:py-12 w-full rounded-[32px]">
        {/* Card Institucional - Layout duas colunas */}
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 items-start lg:items-center w-full">
          {/* Content Texto Institucional - Lado Esquerdo */}
          <div className="flex-1 flex flex-col gap-6 lg:gap-8 items-start min-w-0">
            {/* Heading & Subheading */}
            <div className="flex flex-col gap-3 items-start justify-center px-4 lg:px-10 py-0 w-full">
              {/* Logo da Empresa */}
              <img 
                src="/logo-client.png" 
                alt="Logo DeSordi" 
                className="h-8 w-auto object-contain"
              />
              <h2 className="w-full font-black text-neutral-black text-2xl lg:text-[32px] lg:leading-[1.3] leading-normal">
                {title}
              </h2>
              <p className="w-full font-normal text-neutral-gray text-base lg:text-xl leading-normal">
                {description}
              </p>
            </div>

            {/* Features - Cada feature em seu próprio bloco */}
            {features.map(({ icon: Icon, title, description }, index) => (
              <div
                key={index}
                className="flex flex-col gap-3 lg:gap-4 items-start px-4 lg:px-10 py-0 w-full"
              >
                <div className="flex gap-3 lg:gap-5 items-center px-4 lg:px-6 py-3 lg:py-4 rounded-2xl lg:rounded-3xl w-full">
                  <div className="bg-white flex items-center justify-center p-3 lg:p-4 rounded-2xl lg:rounded-4xl w-12 h-12 lg:w-14 lg:h-14 flex-shrink-0">
                    <Icon className="w-6 h-6 lg:w-7 lg:h-7 text-neutral-black" />
                  </div>

                  {/* Conteúdo do texto */}
                  <div className="flex flex-col gap-1 items-start flex-1">
                    <h4 className="w-full font-medium text-neutral-black text-base lg:text-lg leading-normal">
                      {title}
                    </h4>
                    <p className="w-full font-normal text-neutral-gray text-sm lg:text-base leading-relaxed">
                      {description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Imagem Institucional - Lado Direito */}
          <div className="w-full lg:w-[600px] h-[240px] lg:h-[420px] flex-shrink-0 px-4 lg:px-0 lg:pl-0">
            <img
              src={image}
              alt={imageAlt}
              className="w-full h-full object-cover rounded-2xl lg:rounded-3xl shadow-sm"
            />
          </div>
        </div>

        {/* Container Contato - seguindo exatamente o Figma */}
        {showContactInfo && (
          <div className="flex flex-col lg:flex-row gap-4 lg:justify-between px-4 lg:px-10 py-4 lg:py-6 w-full lg:max-w-[1166px] relative border-t border-[rgba(0,0,0,0.2)]">
            <div className="flex flex-col gap-1 items-start">
              <span className="font-normal text-neutral-gray text-sm lg:text-base leading-normal">
                {contactInfo[0].label}
              </span>
              <span className="font-bold text-neutral-black text-base lg:text-xl leading-normal">
                {contactInfo[0].value}
              </span>
            </div>
            
            <div className="flex flex-col gap-1 items-start">
              <span className="font-normal text-neutral-gray text-sm lg:text-base leading-normal">
                {contactInfo[1].label}
              </span>
              <span className="font-bold text-neutral-black text-base lg:text-xl leading-normal">
                {contactInfo[1].value}
              </span>
            </div>
            
            <div className="flex flex-col gap-1 items-start">
              <span className="font-normal text-neutral-gray text-sm lg:text-base leading-normal">
                {contactInfo[2].label}
              </span>
              <span className="font-bold text-neutral-black text-base lg:text-xl leading-normal">
                {contactInfo[2].value}
              </span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export { AboutSection, type AboutSectionProps, type AboutFeature, type ContactInfo };
export default AboutSection;